import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, Wallet, Filter, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_TONE = {
    arjuna: "text-amber-300 border-amber-300/30 bg-amber-300/5",
    veda: "text-emerald-300 border-emerald-300/30 bg-emerald-300/5",
    kubera: "text-sky-300 border-sky-300/30 bg-sky-300/5",
};

const KYC_TONE = {
    pending: "text-amber-300",
    approved: "text-emerald-400",
    rejected: "text-red-400",
};

export default function DfiAdmin() {
    const [clients, setClients] = useState([]);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const url = filter
                ? `${API}/api/dfi/clients?product=${filter}`
                : `${API}/api/dfi/clients`;
            const r = await fetch(url);
            const d = await r.json();
            setClients(d.clients || []);
            setTotals(d.totals || {});
        } catch (e) {
            setClients([]);
            setTotals({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = "DFI · Admin";
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    return (
        <div
            className="min-h-screen bg-neutral-950 text-neutral-100"
            data-testid="dfi-admin-page"
        >
            <div className="border-b border-neutral-800">
                <div className="max-w-6xl mx-auto px-6 py-12">
                    <div className="flex items-center justify-between gap-6 flex-wrap">
                        <div>
                            <Link
                                to="/dfi"
                                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-neutral-500 hover:text-neutral-300 mb-6"
                                data-testid="dfi-admin-nav-back"
                            >
                                <ArrowLeft className="h-3 w-3" /> DFI
                            </Link>
                            <div className="text-xs tracking-[0.3em] text-amber-300/80 uppercase mb-3">
                                Operator console
                            </div>
                            <h1 className="text-3xl sm:text-4xl font-light tracking-tight">
                                DFI Admin · Client registry
                            </h1>
                        </div>
                        <button
                            onClick={load}
                            data-testid="dfi-admin-refresh"
                            className="inline-flex items-center gap-2 border border-neutral-700 px-5 py-2.5 text-xs tracking-[0.2em] uppercase text-neutral-300 hover:border-amber-300/40 hover:text-amber-300 transition-colors"
                        >
                            <RefreshCw
                                className={`h-3 w-3 ${
                                    loading ? "animate-spin" : ""
                                }`}
                            />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Totals strip */}
            <div className="border-b border-neutral-800">
                <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-4 gap-6">
                    <SummaryTile
                        icon={<Users className="h-4 w-4 text-amber-300/80" />}
                        label="Active clients"
                        value={totals.count || 0}
                        testid="dfi-admin-total-clients"
                    />
                    <SummaryTile
                        icon={<Wallet className="h-4 w-4 text-amber-300/80" />}
                        label="Total AUM (USDT)"
                        value={(totals.aum_usdt || 0).toLocaleString()}
                        testid="dfi-admin-total-aum"
                    />
                    {["arjuna", "veda", "kubera"].map((prod) => {
                        const d = (totals.by_product || {})[prod] || {
                            count: 0,
                            aum_usdt: 0,
                        };
                        return null;
                    })}
                    <ByProductMini
                        totals={totals.by_product || {}}
                    />
                    <div className="hidden md:block" />
                </div>
            </div>

            {/* Filter + table */}
            <div className="max-w-6xl mx-auto px-6 py-10">
                <div className="flex items-center gap-3 mb-6 text-xs">
                    <Filter className="h-3 w-3 text-neutral-500" />
                    <span className="text-neutral-500 tracking-[0.2em] uppercase">
                        Filter
                    </span>
                    <button
                        onClick={() => setFilter("")}
                        data-testid="dfi-admin-filter-all"
                        className={`px-3 py-1.5 border ${
                            filter === ""
                                ? "border-amber-300/60 text-amber-300"
                                : "border-neutral-800 text-neutral-500 hover:text-neutral-300"
                        }`}
                    >
                        All
                    </button>
                    {["arjuna", "veda", "kubera"].map((p) => (
                        <button
                            key={p}
                            onClick={() => setFilter(p)}
                            data-testid={`dfi-admin-filter-${p}`}
                            className={`px-3 py-1.5 border uppercase tracking-wider ${
                                filter === p
                                    ? PRODUCT_TONE[p]
                                    : "border-neutral-800 text-neutral-500 hover:text-neutral-300"
                            }`}
                        >
                            {p}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div
                        className="text-sm text-neutral-500 py-16 text-center"
                        data-testid="dfi-admin-loading"
                    >
                        Loading client registry…
                    </div>
                ) : clients.length === 0 ? (
                    <div
                        className="text-sm text-neutral-500 py-16 text-center border border-dashed border-neutral-800"
                        data-testid="dfi-admin-empty"
                    >
                        No clients onboarded yet. First subscription will
                        appear here.
                    </div>
                ) : (
                    <div
                        className="overflow-x-auto border border-neutral-800"
                        data-testid="dfi-admin-table"
                    >
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 border-b border-neutral-800">
                                    <Th>Client ID</Th>
                                    <Th>Name</Th>
                                    <Th>Jurisdiction</Th>
                                    <Th>Product</Th>
                                    <Th className="text-right">Principal</Th>
                                    <Th className="text-right">HWM</Th>
                                    <Th>KYC</Th>
                                    <Th>Created</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map((c) => (
                                    <tr
                                        key={c.client_id}
                                        data-testid={`dfi-admin-row-${c.client_id}`}
                                        className="border-b border-neutral-900 hover:bg-neutral-900/40"
                                    >
                                        <Td className="font-mono text-[11px] text-amber-300/80">
                                            {c.client_id}
                                        </Td>
                                        <Td>{c.name}</Td>
                                        <Td className="text-neutral-400">
                                            {c.jurisdiction}
                                        </Td>
                                        <Td>
                                            <span
                                                className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                                                    PRODUCT_TONE[c.product] ||
                                                    "border-neutral-700 text-neutral-400"
                                                }`}
                                            >
                                                {c.product}
                                            </span>
                                        </Td>
                                        <Td className="text-right text-neutral-200">
                                            {(c.principal_usdt || 0).toLocaleString()}
                                        </Td>
                                        <Td className="text-right text-neutral-400">
                                            {(c.hwm_usdt || 0).toLocaleString()}
                                        </Td>
                                        <Td
                                            className={`uppercase text-[11px] tracking-wider ${
                                                KYC_TONE[c.kyc_status] ||
                                                "text-neutral-400"
                                            }`}
                                        >
                                            {c.kyc_status}
                                        </Td>
                                        <Td className="text-neutral-500 text-[11px]">
                                            {c.created_at
                                                ? new Date(c.created_at)
                                                      .toISOString()
                                                      .slice(0, 10)
                                                : "—"}
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function SummaryTile({ icon, label, value, testid }) {
    return (
        <div
            className="border border-neutral-800 bg-neutral-900/40 p-5"
            data-testid={testid}
        >
            <div className="flex items-center gap-2 mb-3">
                {icon}
                <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                    {label}
                </span>
            </div>
            <div className="text-2xl font-light text-neutral-100">
                {value}
            </div>
        </div>
    );
}

function ByProductMini({ totals }) {
    const prods = ["arjuna", "veda", "kubera"];
    return (
        <div
            className="border border-neutral-800 bg-neutral-900/40 p-5 md:col-span-2"
            data-testid="dfi-admin-by-product"
        >
            <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-3">
                AUM by product
            </div>
            <div className="grid grid-cols-3 gap-4">
                {prods.map((p) => {
                    const d = totals[p] || { count: 0, aum_usdt: 0 };
                    return (
                        <div key={p}>
                            <div
                                className={`text-[10px] uppercase tracking-wider mb-1 ${
                                    PRODUCT_TONE[p].split(" ")[0]
                                }`}
                            >
                                {p}
                            </div>
                            <div className="text-base font-light text-neutral-100">
                                {d.aum_usdt.toLocaleString()}
                            </div>
                            <div className="text-[10px] text-neutral-500">
                                {d.count} client
                                {d.count === 1 ? "" : "s"}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function Th({ children, className = "" }) {
    return (
        <th className={`px-4 py-3 text-left font-medium ${className}`}>
            {children}
        </th>
    );
}

function Td({ children, className = "" }) {
    return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
